import { Component, ErrorInfo, ReactNode } from 'react';
import AppErrorFallback from '@/components/AppErrorFallback/AppErrorFallback';
import { logClientError } from '@/services/logClientError';

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type AppErrorBoundaryState = {
  hasError: boolean;
  errorId: string;
};

const createId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `err-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      errorId: '',
    };
  }

  static getDerivedStateFromError() {
    return {
      hasError: true,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorId = createId();
    const traceId = createId();

    this.setState({ errorId });

    logClientError({
      errorId,
      traceId,
      message: error.message,
      errorName: error.name,
      stack: error.stack,
      componentStack: errorInfo.componentStack ?? undefined,
      url: window.location.href,
      userAgent: window.navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
  }

  static handleReload = () => {
    window.location.reload();
  };

  override render() {
    const { hasError, errorId } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <AppErrorFallback
          errorId={errorId || 'unavailable'}
          onReload={AppErrorBoundary.handleReload}
        />
      );
    }

    return children;
  }
}

export default AppErrorBoundary;
